import { useAction, useMutation, useQuery } from "convex/react";
import { useEffect, useState } from "react";

import { ContentHooks } from "@/components/Grow/ContentHooks";
import { GROW_EVENTS } from "@/constants/analyticsEvents";
import { Id } from "~/convex/_generated/dataModel";
import { api } from "~/convex/_generated/api";
import { createFileRoute } from "@tanstack/react-router";
import { useGrowStore } from "@/state/useGrowStore";
import { useMixpanel } from "@/hooks/useMixpanel";
import { useToast } from "@/components/ui/use-toast";

interface Hook {
  id: string;
  text: string;
  category: string;
  reasoning: string;
  potentialPaths?: string[];
  topic?: string;
  isLiked?: boolean;
  sources?: Array<{
    id: number;
    title: string;
    url: string;
    snippet: string;
  }>;
}

export const Route = createFileRoute("/_app/_auth/grow/content-producer")({
  component: ContentProducerPage,
});

function ContentProducerPage() {
  const { toast } = useToast();
  const { track } = useMixpanel();

  // Get state from Zustand store
  const { getSelectedAccountData } = useGrowStore();

  // Get selected account data
  const selectedAccountData = getSelectedAccountData();

  const [customPrompt, setCustomPrompt] = useState("");
  const [isGeneratingHooks, setIsGeneratingHooks] = useState(false);
  const [displayedHooks, setDisplayedHooks] = useState<Hook[]>([]);
  const [creatorProfile, setCreatorProfile] = useState<{
    _id: Id<"exp__creatorProfile">;
    handle: string;
    platform: string;
    name: string;
    bio: string;
    followers: number;
    videos: number;
    image?: string;
    contentNiche?: string[];
    topContentTopics?: string[];
    contentStyle?: string;
    audienceAnalysis?: string;
  } | null>(null);
  const [profileHandle, setProfileHandle] = useState<string>("");
  const [profilePlatform, setProfilePlatform] = useState<string>("");
  const [likedHooks, setLikedHooks] = useState<Set<string>>(new Set());
  const [isGeneratingWithSearch, setIsGeneratingWithSearch] = useState(false);
  const [searchResults, setSearchResults] = useState<{
    queries: string[];
    summary: string;
    sources: Array<{
      id: number;
      title: string;
      url: string;
      snippet: string;
    }>;
  } | null>(null);
  const [isSearchEnabled, setIsSearchEnabled] = useState(false);

  // Actions for backend processing
  const generateHooksAction = useAction(api.generate.exp__generateHooks);
  const generateHooksWithSearchAction = useAction(
    api.generate.exp__generateHooksWithSearch,
  );

  // Mutations for liking/unliking hooks
  const likeHookMutation = useMutation(api.exp__creatorInsights.exp__likeHook);
  const unlikeHookMutation = useMutation(
    api.exp__creatorInsights.exp__unlikeHook,
  );
  const markAsFilmedMutation = useMutation(
    api.exp__creatorInsights.exp__markHookAsFilmed,
  );
  const unmarkAsFilmedMutation = useMutation(
    api.exp__creatorInsights.exp__unmarkHookAsFilmed,
  );
  const markAsPostedMutation = useMutation(
    api.exp__creatorInsights.exp__markHookAsPosted,
  );
  const unmarkAsPostedMutation = useMutation(
    api.exp__creatorInsights.exp__unmarkHookAsPosted,
  );

  // Query the creator profile if we have a handle and platform
  const existingProfile = useQuery(
    api.exp__creatorInsights.exp__getCreatorProfile,
    profileHandle && profilePlatform
      ? { handle: profileHandle, platform: profilePlatform }
      : "skip",
  );

  // Query previously liked hooks to initialize the liked state
  const previouslyLikedHooks = useQuery(
    api.exp__creatorInsights.exp__getLikedHooks,
    creatorProfile?._id
      ? { creatorId: creatorProfile._id, limit: 100 }
      : "skip",
  );

  // State for tracking filmed and posted hooks
  const [filmedHooks, setFilmedHooks] = useState<Set<string>>(new Set());
  const [postedHooks, setPostedHooks] = useState<Set<string>>(new Set());

  // Update profile handle when account selection changes - THIS IS KEY FOR FIXING THE ISSUE
  useEffect(() => {
    if (selectedAccountData) {
      const handle = selectedAccountData.handle.replace("@", "");
      setProfileHandle(handle);
      setProfilePlatform(selectedAccountData.platform);

      // Clear existing profile data when switching accounts
      setCreatorProfile(null);
    } else {
      setProfileHandle("");
      setProfilePlatform("");
      setCreatorProfile(null);
    }
    // Always reset hooks when switching accounts
    setDisplayedHooks([]);
  }, [selectedAccountData]);

  // Update creator profile when existing profile data is loaded or reset when none found
  useEffect(() => {
    if (existingProfile) {
      setCreatorProfile(existingProfile);
    } else if (profileHandle && profilePlatform) {
      // If we have handle and platform but no existing profile, reset the state
      setCreatorProfile(null);
    }
  }, [existingProfile, profileHandle, profilePlatform]);

  // Initialize liked hooks when previously liked hooks are loaded
  useEffect(() => {
    if (previouslyLikedHooks) {
      const likedHookTexts = new Set(
        previouslyLikedHooks.map((hook) => hook.hookText),
      );
      setLikedHooks(likedHookTexts);

      // Initialize filmed hooks
      const filmedHookTexts = new Set(
        previouslyLikedHooks
          .filter((hook) => hook.isFilmed && !hook.isPosted)
          .map((hook) => hook.hookText),
      );
      setFilmedHooks(filmedHookTexts);

      // Initialize posted hooks
      const postedHookTexts = new Set(
        previouslyLikedHooks
          .filter((hook) => hook.isPosted)
          .map((hook) => hook.hookText),
      );
      setPostedHooks(postedHookTexts);
    }
  }, [previouslyLikedHooks]);

  // Auto-generate hooks when creator profile is ready and no hooks are displayed
  useEffect(() => {
    if (
      creatorProfile?.contentNiche &&
      creatorProfile?.topContentTopics &&
      creatorProfile?.contentStyle &&
      creatorProfile?.audienceAnalysis &&
      displayedHooks.length === 0 &&
      !isGeneratingHooks &&
      !isGeneratingWithSearch
    ) {
      // Generate initial 10 hooks
      handleGenerateHooks();
    }
  }, [
    creatorProfile,
    displayedHooks.length,
    isGeneratingHooks,
    isGeneratingWithSearch,
  ]);

  // Auto-generate more hooks when running low
  useEffect(() => {
    if (
      creatorProfile &&
      displayedHooks.length > 0 && // Only if we have some hooks already
      displayedHooks.length < 5 && // Generate when under 5 hooks
      !isGeneratingHooks &&
      !isGeneratingWithSearch
    ) {
      generateMoreHooks();
    }
  }, [
    displayedHooks.length,
    isGeneratingHooks,
    isGeneratingWithSearch,
    creatorProfile,
  ]);

  const handleGenerateHooks = async (direction?: string) => {
    if (!creatorProfile) {
      toast({
        title: "Profile Analysis Required",
        description:
          "Please analyze the profile first before generating hooks.",
        variant: "destructive",
      });
      return;
    }

    const promptToUse = direction || customPrompt;

    // Check if search is enabled and custom prompt is required
    if (isSearchEnabled && !promptToUse.trim()) {
      toast({
        title: "Topic Required for Search",
        description:
          "Please enter a specific topic when using search-powered generation.",
        variant: "destructive",
      });
      return;
    }

    const isUsingSearch = isSearchEnabled && promptToUse.trim();

    // Track hook generation start with daily tracking
    const today = new Date().toISOString().split("T")[0]; // YYYY-MM-DD format
    const dailyBatchCountKey = `hookBatchCount_${today}_${creatorProfile._id}`;
    const lastGenerationDateKey = `lastHookGenerationDate_${creatorProfile._id}`;

    const currentBatchCount = parseInt(
      localStorage.getItem(dailyBatchCountKey) || "0",
    );
    const lastGenerationDate = localStorage.getItem(lastGenerationDateKey);
    const isFirstBatchToday = lastGenerationDate !== today;

    track(GROW_EVENTS.HOOK_BATCH_GENERATED, {
      creator_profile_id: creatorProfile._id,
      creator_handle: creatorProfile.handle,
      creator_platform: creatorProfile.platform,
      content_niche: creatorProfile.contentNiche,
      has_custom_direction: !!promptToUse.trim(),
      custom_direction_length: promptToUse.trim().length,
      is_using_search: isUsingSearch,
      existing_hooks_count: displayedHooks.length,
      // Daily tracking fields
      generation_date: today,
      daily_batch_number: currentBatchCount + 1,
      is_first_batch_today: isFirstBatchToday,
      is_daily_goal_met: currentBatchCount >= 0, // At least 1 batch per day
    });

    if (isUsingSearch) {
      setIsGeneratingWithSearch(true);
    } else {
      setIsGeneratingHooks(true);
    }
    setSearchResults(null); // Clear previous search results

    try {
      let result;

      if (isUsingSearch) {
        result = await generateHooksWithSearchAction({
          creatorId: creatorProfile._id,
          direction: promptToUse,
        });
      } else {
        result = await generateHooksAction({
          creatorId: creatorProfile._id,
          direction: promptToUse || undefined,
        });
      }

      const hooksWithIds = result.hooks.map(
        (
          hook: {
            text: string;
            category: string;
            reasoning: string;
            potentialPaths?: string[];
            sources?: Array<{
              id: number;
              title: string;
              url: string;
              snippet: string;
            }>;
          },
          index: number,
        ) => ({
          ...hook,
          id: `hook_${isUsingSearch ? "search_" : ""}${Date.now()}_${index}`,
        }),
      );

      // Set the first 10 hooks as displayed
      setDisplayedHooks(hooksWithIds.slice(0, 10));

      // Store search results if using search
      if (isUsingSearch && "searchQueries" in result) {
        const searchResult = result as unknown as {
          hooks: Array<{
            text: string;
            category: string;
            reasoning: string;
            potentialPaths: string[];
            topic: string;
            sources?: Array<{
              id: number;
              title: string;
              url: string;
              snippet: string;
            }>;
          }>;
          searchQueries: string[];
          searchResults: string;
          sources: Array<{
            id: number;
            title: string;
            url: string;
            snippet: string;
          }>;
        };

        // If no sources are returned but we have search queries, create placeholder sources
        let finalSources = searchResult.sources || [];
        if (
          finalSources.length === 0 &&
          searchResult.searchQueries.length > 0
        ) {
          finalSources = [
            {
              id: 1,
              title: "Web Search Results",
              url: "#",
              snippet: "AI used real-time web search to generate factual hooks",
            },
          ];
        }

        setSearchResults({
          queries: searchResult.searchQueries,
          summary: searchResult.searchResults,
          sources: finalSources,
        });
      }

      // Update daily batch count
      const newBatchCount = currentBatchCount + 1;
      localStorage.setItem(dailyBatchCountKey, newBatchCount.toString());
      localStorage.setItem(lastGenerationDateKey, today);

      // Track successful hook generation
      track(GROW_EVENTS.HOOK_GENERATION_COMPLETED, {
        creator_profile_id: creatorProfile._id,
        creator_handle: creatorProfile.handle,
        creator_platform: creatorProfile.platform,
        content_niche: creatorProfile.contentNiche,
        has_custom_direction: !!promptToUse.trim(),
        custom_direction_length: promptToUse.trim().length,
        is_using_search: isUsingSearch,
        hooks_generated_count: hooksWithIds.length,
        hooks_displayed_count: hooksWithIds.slice(0, 10).length,
        has_search_results: isUsingSearch && !!searchResults,
        search_queries_count:
          isUsingSearch && "searchQueries" in result
            ? (result as { searchQueries?: string[] }).searchQueries?.length ||
              0
            : 0,
        // Daily tracking fields
        generation_date: today,
        daily_batch_number: newBatchCount,
        is_first_batch_today: isFirstBatchToday,
        total_batches_today: newBatchCount,
      });

      toast({
        title: isUsingSearch
          ? "Search-Powered Hooks Generated! 🔍"
          : "Hooks Generated!",
        description: isUsingSearch
          ? `Generated ${hooksWithIds.length} fact-based hooks using real-time search data.`
          : `Generated ${hooksWithIds.length} unique content hooks.`,
      });
    } catch (error) {
      console.error("Error generating hooks:", error);
      toast({
        title: isUsingSearch
          ? "Search-Powered Generation Failed"
          : "Hook Generation Failed",
        description: "Failed to generate hooks. Please try again.",
        variant: "destructive",
      });
    } finally {
      if (isUsingSearch) {
        setIsGeneratingWithSearch(false);
      } else {
        setIsGeneratingHooks(false);
      }
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      // Track hook copy action
      track(GROW_EVENTS.HOOK_COPIED, {
        action: "copy_to_clipboard",
        hook_text: text,
        creator_profile_id: creatorProfile?._id,
        creator_handle: creatorProfile?.handle,
        creator_platform: creatorProfile?.platform,
        timestamp: new Date().toISOString(),
        source_stage: "unknown", // Could be from any stage
        interaction_type: "copy",
      });

      await navigator.clipboard.writeText(text);
      toast({
        title: "Copied to clipboard!",
        description: "Hook has been copied to your clipboard.",
      });
    } catch (err) {
      toast({
        title: "Failed to copy",
        description: "Please try selecting and copying the text manually.",
        variant: "destructive",
      });
    }
  };

  const handleLikeHook = async (hook: Hook) => {
    if (!creatorProfile) return;

    try {
      const isCurrentlyLiked = likedHooks.has(hook.text);

      // Track hook action
      track(GROW_EVENTS.HOOK_COPIED, {
        action: isCurrentlyLiked ? "unlike" : "like",
        hook_text: hook.text,
        hook_category: hook.category,
        hook_id: hook.id,
        creator_profile_id: creatorProfile._id,
        creator_handle: creatorProfile.handle,
        creator_platform: creatorProfile.platform,
        timestamp: new Date().toISOString(),
        source_stage: "generated",
        target_stage: isCurrentlyLiked ? "generated" : "liked",
        has_custom_direction: !!customPrompt?.trim(),
        hook_sources: hook.sources?.length || 0,
        is_search_generated: hook.id.includes("search_"),
      });

      if (isCurrentlyLiked) {
        // Unlike the hook
        await unlikeHookMutation({
          creatorId: creatorProfile._id,
          hookText: hook.text,
        });
        setLikedHooks((prev) => {
          const newSet = new Set(prev);
          newSet.delete(hook.text);
          return newSet;
        });
        toast({
          title: "Hook unliked",
          description: "Removed from your liked hooks.",
        });
      } else {
        // Like the hook
        await likeHookMutation({
          creatorId: creatorProfile._id,
          hookText: hook.text,
          hookCategory: hook.category,
          hookReasoning: hook.reasoning,
          potentialPaths: hook.potentialPaths,
          hookTopic: hook.topic,
          originalDirection: customPrompt || undefined,
        });
        setLikedHooks((prev) => new Set(prev).add(hook.text));

        // Remove the hook from displayed hooks
        setDisplayedHooks((prev) => prev.filter((h) => h.id !== hook.id));

        toast({
          title: "Hook liked! 👍",
          description:
            "Added to your liked hooks. This will help improve your future suggestions.",
        });
      }
    } catch (error) {
      console.error("Error liking/unliking hook:", error);
      toast({
        title: "Error",
        description: "Failed to update hook preference. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleMarkAsFilmed = async (hookText: string) => {
    if (!creatorProfile) return;

    try {
      // Track hook action
      track(GROW_EVENTS.HOOK_COPIED, {
        action: "mark_as_filmed",
        hook_text: hookText,
        creator_profile_id: creatorProfile._id,
        creator_handle: creatorProfile.handle,
        creator_platform: creatorProfile.platform,
        timestamp: new Date().toISOString(),
        source_stage: "liked",
        target_stage: "filmed",
      });

      await markAsFilmedMutation({
        creatorId: creatorProfile._id,
        hookText: hookText,
      });
      setFilmedHooks((prev) => new Set(prev).add(hookText));
      toast({
        title: "Hook marked as filmed! 🎬",
        description: "Moved to your filmed hooks.",
      });
    } catch (error) {
      console.error("Error marking hook as filmed:", error);
      toast({
        title: "Error",
        description: "Failed to mark hook as filmed. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleUnmarkAsFilmed = async (hookText: string) => {
    if (!creatorProfile) return;

    try {
      await unmarkAsFilmedMutation({
        creatorId: creatorProfile._id,
        hookText: hookText,
      });
      setFilmedHooks((prev) => {
        const newSet = new Set(prev);
        newSet.delete(hookText);
        return newSet;
      });
      toast({
        title: "Hook unmarked as filmed",
        description: "Moved back to liked hooks.",
      });
    } catch (error) {
      console.error("Error unmarking hook as filmed:", error);
      toast({
        title: "Error",
        description: "Failed to unmark hook as filmed. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleUnlikeFromLikedList = async (hookText: string) => {
    if (!creatorProfile) return;

    try {
      await unlikeHookMutation({
        creatorId: creatorProfile._id,
        hookText: hookText,
      });
      setLikedHooks((prev) => {
        const newSet = new Set(prev);
        newSet.delete(hookText);
        return newSet;
      });
      toast({
        title: "Hook removed",
        description: "Removed from your liked hooks.",
      });
    } catch (error) {
      console.error("Error removing liked hook:", error);
      toast({
        title: "Error",
        description: "Failed to remove hook. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleRemoveHook = async (hookId: string) => {
    // Remove the hook immediately
    setDisplayedHooks((prev) => prev.filter((hook) => hook.id !== hookId));
  };

  // Drag and drop handlers
  const handleMoveHookToLiked = async (hook: Hook) => {
    if (!creatorProfile) return;

    try {
      // Track hook action
      track(GROW_EVENTS.HOOK_COPIED, {
        action: "drag_to_liked",
        hook_text: hook.text,
        hook_category: hook.category,
        hook_id: hook.id,
        creator_profile_id: creatorProfile._id,
        creator_handle: creatorProfile.handle,
        creator_platform: creatorProfile.platform,
        timestamp: new Date().toISOString(),
        source_stage: "generated",
        target_stage: "liked",
        interaction_type: "drag_and_drop",
        has_custom_direction: !!customPrompt?.trim(),
        hook_sources: hook.sources?.length || 0,
        is_search_generated: hook.id.includes("search_"),
      });

      // Like the hook (same as clicking the like button)
      await likeHookMutation({
        creatorId: creatorProfile._id,
        hookText: hook.text,
        hookCategory: hook.category,
        hookReasoning: hook.reasoning,
        potentialPaths: hook.potentialPaths,
        originalDirection: customPrompt || undefined,
      });

      setLikedHooks((prev) => new Set(prev).add(hook.text));

      // Remove the hook from displayed hooks
      setDisplayedHooks((prev) => prev.filter((h) => h.id !== hook.id));

      toast({
        title: "Hook moved to liked! 👍",
        description: "Hook has been moved to your liked column.",
      });
    } catch (error) {
      console.error("Error moving hook to liked:", error);
      toast({
        title: "Error",
        description: "Failed to move hook. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleMoveHookToFilmed = async (hookText: string) => {
    if (!creatorProfile) return;

    try {
      // Mark as filmed (same as clicking the film button)
      await markAsFilmedMutation({
        creatorId: creatorProfile._id,
        hookText: hookText,
      });

      setFilmedHooks((prev) => new Set(prev).add(hookText));

      toast({
        title: "Hook moved to filmed! 🎬",
        description: "Hook has been moved to your filmed column.",
      });
    } catch (error) {
      console.error("Error moving hook to filmed:", error);
      toast({
        title: "Error",
        description: "Failed to move hook. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleMoveFilmedToLiked = async (hookText: string) => {
    if (!creatorProfile) return;

    try {
      // Unmark as filmed (same as clicking the unmark button)
      await unmarkAsFilmedMutation({
        creatorId: creatorProfile._id,
        hookText: hookText,
      });

      setFilmedHooks((prev) => {
        const newSet = new Set(prev);
        newSet.delete(hookText);
        return newSet;
      });

      toast({
        title: "Hook moved back to liked! 👍",
        description: "Hook has been moved back to your liked column.",
      });
    } catch (error) {
      console.error("Error moving filmed hook to liked:", error);
      toast({
        title: "Error",
        description: "Failed to move hook. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleMoveHookToPosted = async (hookText: string) => {
    if (!creatorProfile) return;

    try {
      // Mark as posted
      await markAsPostedMutation({
        creatorId: creatorProfile._id,
        hookText: hookText,
      });

      setPostedHooks((prev) => new Set(prev).add(hookText));
      // Remove from filmed hooks if it was there
      setFilmedHooks((prev) => {
        const newSet = new Set(prev);
        newSet.delete(hookText);
        return newSet;
      });

      toast({
        title: "Hook moved to posted! 📱",
        description: "Hook has been moved to your posted column.",
      });
    } catch (error) {
      console.error("Error moving hook to posted:", error);
      toast({
        title: "Error",
        description: "Failed to move hook. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleMovePostedToFilmed = async (hookText: string) => {
    if (!creatorProfile) return;

    try {
      // Unmark as posted (this will also delete analytics)
      await unmarkAsPostedMutation({
        creatorId: creatorProfile._id,
        hookText: hookText,
      });

      setPostedHooks((prev) => {
        const newSet = new Set(prev);
        newSet.delete(hookText);
        return newSet;
      });
      // Add back to filmed hooks
      setFilmedHooks((prev) => new Set(prev).add(hookText));

      toast({
        title: "Hook moved back to filmed! 🎬",
        description:
          "Hook has been moved back to your filmed column and analytics have been removed.",
      });
    } catch (error) {
      console.error("Error moving posted hook to filmed:", error);
      toast({
        title: "Error",
        description: "Failed to move hook. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleMarkAsPosted = async (hookText: string) => {
    if (!creatorProfile) return;

    try {
      // Track hook action
      track(GROW_EVENTS.HOOK_COPIED, {
        action: "mark_as_posted",
        hook_text: hookText,
        creator_profile_id: creatorProfile._id,
        creator_handle: creatorProfile.handle,
        creator_platform: creatorProfile.platform,
        timestamp: new Date().toISOString(),
        source_stage: filmedHooks.has(hookText) ? "filmed" : "liked",
        target_stage: "posted",
      });

      await markAsPostedMutation({
        creatorId: creatorProfile._id,
        hookText: hookText,
      });
      setPostedHooks((prev) => new Set(prev).add(hookText));
      // Remove from filmed hooks if it was there
      setFilmedHooks((prev) => {
        const newSet = new Set(prev);
        newSet.delete(hookText);
        return newSet;
      });
      toast({
        title: "Hook marked as posted! 📱",
        description: "Moved to your posted hooks.",
      });
    } catch (error) {
      console.error("Error marking hook as posted:", error);
      toast({
        title: "Error",
        description: "Failed to mark hook as posted. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleUnmarkAsPosted = async (hookText: string) => {
    if (!creatorProfile) return;

    try {
      await unmarkAsPostedMutation({
        creatorId: creatorProfile._id,
        hookText: hookText,
      });
      setPostedHooks((prev) => {
        const newSet = new Set(prev);
        newSet.delete(hookText);
        return newSet;
      });
      // Add back to filmed hooks
      setFilmedHooks((prev) => new Set(prev).add(hookText));
      toast({
        title: "Hook unmarked as posted",
        description: "Moved back to filmed hooks and analytics removed.",
      });
    } catch (error) {
      console.error("Error unmarking hook as posted:", error);
      toast({
        title: "Error",
        description: "Failed to unmark hook as posted. Please try again.",
        variant: "destructive",
      });
    }
  };

  const generateMoreHooks = async () => {
    if (!creatorProfile || isGeneratingHooks || isGeneratingWithSearch) return;

    const isUsingSearch = isSearchEnabled && customPrompt.trim();

    try {
      if (isUsingSearch) {
        setIsGeneratingWithSearch(true);
      } else {
        setIsGeneratingHooks(true);
      }

      let result;

      if (isUsingSearch) {
        result = await generateHooksWithSearchAction({
          creatorId: creatorProfile._id,
          direction: customPrompt,
        });
      } else {
        result = await generateHooksAction({
          creatorId: creatorProfile._id,
          direction: customPrompt || undefined,
        });
      }

      const hooksWithIds = result.hooks.map(
        (
          hook: {
            text: string;
            category: string;
            reasoning: string;
            potentialPaths?: string[];
            sources?: Array<{
              id: number;
              title: string;
              url: string;
              snippet: string;
            }>;
          },
          index: number,
        ) => ({
          ...hook,
          id: `hook_${isUsingSearch ? "search_" : ""}${Date.now()}_${index}`,
        }),
      );

      // Append the first 10 new hooks to existing displayed hooks
      setDisplayedHooks((prev) => [...prev, ...hooksWithIds.slice(0, 10)]);

      // Track successful additional hook generation
      track(GROW_EVENTS.ADDITIONAL_HOOKS_GENERATED, {
        creator_profile_id: creatorProfile._id,
        creator_handle: creatorProfile.handle,
        creator_platform: creatorProfile.platform,
        content_niche: creatorProfile.contentNiche,
        has_custom_direction: !!customPrompt.trim(),
        custom_direction_length: customPrompt.trim().length,
        is_using_search: isUsingSearch,
        hooks_generated_count: hooksWithIds.length,
        hooks_added_count: hooksWithIds.slice(0, 10).length,
        total_hooks_after:
          displayedHooks.length + hooksWithIds.slice(0, 10).length,
        trigger: "auto_refill",
      });

      // Update search results if using search
      if (isUsingSearch && "searchQueries" in result) {
        const searchResult = result as unknown as {
          hooks: Array<{
            text: string;
            category: string;
            reasoning: string;
            potentialPaths: string[];
            topic: string;
            sources?: Array<{
              id: number;
              title: string;
              url: string;
              snippet: string;
            }>;
          }>;
          searchQueries: string[];
          searchResults: string;
          sources: Array<{
            id: number;
            title: string;
            url: string;
            snippet: string;
          }>;
        };
        // If no sources are returned but we have search queries, create placeholder sources
        let finalSources = searchResult.sources || [];
        if (
          finalSources.length === 0 &&
          searchResult.searchQueries.length > 0
        ) {
          finalSources = [
            {
              id: 1,
              title: "Web Search Results",
              url: "#",
              snippet: "AI used real-time web search to generate factual hooks",
            },
          ];
        }

        setSearchResults({
          queries: searchResult.searchQueries,
          summary: searchResult.searchResults,
          sources: finalSources,
        });
      } else if (!isUsingSearch) {
        // Clear search results if not using search
        setSearchResults(null);
      }
    } catch (error) {
      console.error("Error generating more hooks:", error);
      // Silently fail - user can still use existing hooks
    } finally {
      if (isUsingSearch) {
        setIsGeneratingWithSearch(false);
      } else {
        setIsGeneratingHooks(false);
      }
    }
  };

  // Show message if no profile is ready
  if (
    !creatorProfile ||
    !creatorProfile.contentNiche ||
    !creatorProfile.topContentTopics ||
    !creatorProfile.contentStyle ||
    !creatorProfile.audienceAnalysis
  ) {
    return (
      <div className="w-full">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            Content Producer
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            Personalised content hooks for your social media
          </p>
        </div>

        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-8 text-center">
          <h3 className="text-xl font-semibold text-blue-900 dark:text-blue-100 mb-2">
            Profile Analysis Required
          </h3>
          <p className="text-blue-700 dark:text-blue-300 mb-4">
            You need to complete your profile analysis before generating content
            hooks.
          </p>
          <p className="text-sm text-blue-600 dark:text-blue-400">
            Click "My Profile" in the sidebar to analyze your social media
            account first.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <ContentHooks
        displayedHooks={displayedHooks}
        likedHooks={likedHooks}
        filmedHooks={filmedHooks}
        postedHooks={postedHooks}
        previouslyLikedHooks={previouslyLikedHooks}
        customPrompt={customPrompt}
        isGeneratingHooks={isGeneratingHooks}
        isGeneratingWithSearch={isGeneratingWithSearch}
        isSearchEnabled={isSearchEnabled}
        searchResults={searchResults}
        creatorProfile={creatorProfile}
        onCustomPromptChange={setCustomPrompt}
        onSearchEnabledChange={setIsSearchEnabled}
        onGenerateHooks={handleGenerateHooks}
        onLikeHook={handleLikeHook}
        onUnlikeFromLikedList={handleUnlikeFromLikedList}
        onMarkAsFilmed={handleMarkAsFilmed}
        onMarkAsPosted={handleMarkAsPosted}
        onUnmarkAsFilmed={handleUnmarkAsFilmed}
        onUnmarkAsPosted={handleUnmarkAsPosted}
        onCopyToClipboard={copyToClipboard}
        onRemoveHook={handleRemoveHook}
        onUpdateDisplayedHook={(hookId, _originalText, newText) => {
          setDisplayedHooks((prev) =>
            prev.map((hook) =>
              hook.id === hookId ? { ...hook, text: newText } : hook,
            ),
          );
        }}
        onMoveHookToLiked={handleMoveHookToLiked}
        onMoveHookToFilmed={handleMoveHookToFilmed}
        onMoveHookToPosted={handleMoveHookToPosted}
        onMoveFilmedToLiked={handleMoveFilmedToLiked}
        onMovePostedToFilmed={handleMovePostedToFilmed}
      />
    </div>
  );
}
